import React, { useEffect, useMemo, useState } from 'react'
import { Autocomplete, CircularProgress, TextField } from '@mui/material'
import { debounce } from 'lodash'
import {
  RequestingOrganisationDto,
  RequestingOrganisationService,
} from '@services/RequestingOrganisationsService'
import { VisitRequestingOrganisationDto } from '@/services/dtos'

interface Props {
  value?: VisitRequestingOrganisationDto | null
  onChange: (org: VisitRequestingOrganisationDto | null) => void
  siteId?: number | string
  label?: string
  size?: 'small' | 'medium'
  disabled?: boolean
}

const RequestingOrganisationSelector: React.FC<Props> = ({
  value = null,
  onChange,
  siteId,
  label = 'Organisation prescriptrice',
  size = 'medium',
  disabled = false,
}) => {
  const [options, setOptions] = useState<RequestingOrganisationDto[]>([])
  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)
  const normalizedSiteId =
    siteId === '' || siteId === undefined || siteId === null
      ? undefined
      : Number(siteId)

  const fetchOptions = useMemo(
    () =>
      debounce((search: string, siteId?: number) => {
        setLoading(true)
        RequestingOrganisationService.findAll({
          search,
          siteId,
          limit: 50,
        })
          .then((res: any) => setOptions(res?.datas ?? []))
          .catch((err) => console.error(err))
          .finally(() => setLoading(false))
      }, 300),
    [],
  )

  useEffect(() => {
    fetchOptions(inputValue, normalizedSiteId)
    return () => {
      fetchOptions.cancel()
    }
  }, [inputValue, normalizedSiteId, fetchOptions])

  useEffect(() => {
    if (
      value &&
      normalizedSiteId !== undefined &&
      value.siteId !== normalizedSiteId
    ) {
      onChange(null)
    }
  }, [normalizedSiteId, value, onChange])

  return (
    <Autocomplete
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInput) => setInputValue(newInput)}
      options={options}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={loading}
      disabled={disabled}
      size={size}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          onFocus={() => fetchOptions(inputValue, normalizedSiteId)}
          InputProps={{
            ...params.InputProps,
            inputProps: {
              ...params.inputProps,
              autoComplete: 'new-password',
              'data-form-type': 'other',
            },
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <div>
            <div style={{ fontWeight: 500 }}>{option.name}</div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>
              {option.city}
            </div>
          </div>
        </li>
      )}
    />
  )
}

export default RequestingOrganisationSelector
